import type { MenuDataItem } from "@ant-design/pro-components";
import { useCallback, useMemo } from "react";
import { ROUTE_PERMISSION_MAP } from "../constants";
import { permissionService } from "../core";

/**
 * 菜单权限 Hook - 专门处理菜单相关的权限逻辑
 */
export const useMenuPermission = () => {
  // 检查单个路径的权限
  const hasPathPermission = useMemo(() => {
    return (path?: string): boolean => {
      if (!path) return true;
      return permissionService.hasRoutePermission(path);
    };
  }, []);

  // 处理单个菜单项的权限（递归函数）
  const processMenuItemPermission = useCallback(
    (item: MenuDataItem): MenuDataItem => {
      const processedItem = { ...item };

      // 检查当前菜单项是否有权限
      const hasPermission = item.path ? hasPathPermission(item.path) : true;

      // 如果有子菜单，递归处理
      if (item.children && item.children.length > 0) {
        processedItem.children = item.children.map((child) =>
          processMenuItemPermission(child)
        );

        // 检查是否有任何可见的子菜单
        const hasVisibleChildren = processedItem.children.some(
          (child) => !child.hideInMenu
        );

        // 如果当前项没有权限且没有可见的子菜单，则隐藏
        if (!hasPermission && !hasVisibleChildren) {
          processedItem.hideInMenu = true;
        }
      } else {
        // 叶子节点，直接根据权限设置是否隐藏
        if (!hasPermission) {
          processedItem.hideInMenu = true;
        }
      }

      return processedItem;
    },
    [hasPathPermission]
  );

  // 过滤菜单数据（兼容 Ant Design Pro 的 MenuDataItem）
  const filterMenuData = useMemo(() => {
    return (menuData: MenuDataItem[]): MenuDataItem[] => {
      return menuData.map((item) => processMenuItemPermission(item));
    };
  }, [processMenuItemPermission]);

  // 获取用户可访问的菜单路径列表
  const getAccessibleMenuPaths = useMemo(() => {
    return (): string[] => {
      return Object.keys(ROUTE_PERMISSION_MAP).filter((path) =>
        hasPathPermission(path)
      );
    };
  }, [hasPathPermission]);

  return {
    hasPathPermission,
    filterMenuData,
    getAccessibleMenuPaths,
    processMenuItemPermission,
  };
};
